import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchScenarioMotionSyncStatus } from '../../../../features/scenarios/api';
import { motionSyncSignature } from '../../../../features/scenarios/motionSyncStatus';
import type { MotionSyncStatus } from '../../../../features/scenarios/types';

type ScenarioSource = Parameters<typeof motionSyncSignature>[0];

/** scenario_id → scene_id → 同期の鮮度。 */
export type MotionSyncStatusMap = Record<string, Record<string, MotionSyncStatus>>;

/**
 * 開いているシナリオの生成済みモーションシーンについて、BGM 同期の鮮度を取得する
 * (docs/features/brand-cut-motion-graphics-plan.md §10.4 / §15.4・Phase 2d)。
 *
 * ポーリングはしない。シナリオの state (lastResult) が更新されたとき (読み込み・BGM の差し替え / 除外・
 * シーンの尺や順序の変更・モーション生成完了後の getScenario 再取得) に、鮮度が変わり得る入力の要約
 * (motionSyncSignature) が変わったシナリオだけを取り直す。取得失敗は表示しない (注意が出ないだけ)。
 */
export function useMotionSyncStatus(scenarios: readonly ScenarioSource[]): MotionSyncStatusMap {
  const [statusMap, setStatusMap] = useState<MotionSyncStatusMap>({});
  // 取得を始めた時点の要約 (scenario_id → signature)。応答時に食い違えば古い応答として捨てる
  const requestedRef = useRef<Record<string, string>>({});

  const signatures = useMemo(() => {
    const out: Record<string, string> = {};
    for (const s of scenarios) {
      if (!s.id) continue;
      const sig = motionSyncSignature(s);
      if (sig) out[s.id] = sig;
    }
    return out;
  }, [scenarios]);
  const signatureKey = JSON.stringify(signatures);

  useEffect(() => {
    const current: Record<string, string> = JSON.parse(signatureKey);
    // 対象が無くなったシナリオの結果は捨てる
    setStatusMap((prev) => {
      const kept = Object.fromEntries(Object.entries(prev).filter(([id]) => id in current));
      return Object.keys(kept).length === Object.keys(prev).length ? prev : kept;
    });
    for (const id of Object.keys(requestedRef.current)) {
      if (!(id in current)) delete requestedRef.current[id];
    }
    for (const [scenarioId, sig] of Object.entries(current)) {
      if (requestedRef.current[scenarioId] === sig) continue;
      requestedRef.current[scenarioId] = sig;
      fetchScenarioMotionSyncStatus(scenarioId)
        .then((scenes) => {
          if (requestedRef.current[scenarioId] !== sig) return;
          setStatusMap((prev) => ({ ...prev, [scenarioId]: scenes }));
        })
        .catch((err) => {
          console.warn('[motion-sync] 同期の鮮度の取得に失敗しました', err);
          // 同じ入力では再試行しない (入力が変われば取り直す)
        });
    }
  }, [signatureKey]);

  return statusMap;
}
